import RAPIER from "@dimforge/rapier3d-deterministic-compat";
import type { MapData } from "../map/mapdata.ts";
import type { Vec3 } from "../math.ts";

let initPromise: Promise<void> | undefined;
/** Rapier's WASM must be initialised once per process/page before creating worlds. */
export function initPhysics(): Promise<void> {
  initPromise ??= RAPIER.init();
  return initPromise;
}

// Collision groups: upper 16 bits = membership, lower 16 bits = filter.
const G_MAP = 0x0001;
const G_PLAYER = 0x0002;
const groups = (member: number, filter: number) => (member << 16) | filter;
export const MAP_ONLY = groups(G_PLAYER, G_MAP);

export interface MoveResult {
  dx: number;
  dy: number;
  dz: number;
  grounded: boolean;
}

/**
 * A solid box that isn't part of the Rapier world: the Convoy payload and the spawn gates.
 * Horizontally oriented: `fx, fz` is the unit direction of its length. Collisions against it are
 * plain arithmetic (+ − × ÷ √, all exactly rounded by IEEE 754), so the server and every
 * client's prediction resolve them bit-identically; adding it to Rapier would change the
 * broad-phase layout and break prediction determinism (see addCapsule).
 */
export interface Obstacle {
  /** Centre of the footprint, at the bottom face. */
  x: number;
  y: number;
  z: number;
  fx: number;
  fz: number;
  /** Half length (along f) and half width. */
  hl: number;
  hw: number;
  /** Height above y. */
  h: number;
}

/** Feet within this of an obstacle's top count as standing on it. */
const TOP_SKIN = 0.05;
/** Feet this far under the top still clear it sideways (the tick's gravity dip while standing). */
const SIDE_SKIN = 0.02;

/**
 * Static map collision + per-player character controllers. Players never collide with each
 * other (keeps client prediction exact); hit detection against players uses hitbox math.
 */
export class PhysicsWorld {
  readonly world: RAPIER.World;
  /** Solid boxes outside Rapier (payload, closed spawn gates); set wholesale by the match layer. */
  obstacles: Obstacle[] = [];
  private readonly cc: RAPIER.KinematicCharacterController;
  private readonly capsules = new Map<number, { col: RAPIER.Collider; radius: number; half: number }>();

  constructor(map: MapData) {
    this.world = new RAPIER.World({ x: 0, y: 0, z: 0 });
    for (const c of map.colliders) {
      const desc = RAPIER.ColliderDesc.trimesh(c.vertices, c.indices).setCollisionGroups(groups(G_MAP, 0xffff));
      this.world.createCollider(desc);
    }
    this.cc = this.world.createCharacterController(0.02);
    this.cc.enableAutostep(0.45, 0.25, false);
    this.cc.enableSnapToGround(0.35);
    this.cc.setMaxSlopeClimbAngle((50 * Math.PI) / 180);
    this.cc.setMinSlopeSlideAngle((60 * Math.PI) / 180);
    this.cc.setApplyImpulsesToDynamicBodies(false);
    // Builds the broad-phase BVH over the static map, once. The world is never stepped again:
    // each step lets Rapier re-optimize that BVH, which changes query traversal order and so,
    // by a hair, the character controller's result against walls. The server steps per tick
    // but a client also steps per replayed input, so stepping made their worlds drift apart.
    // Nothing here needs stepping (the map is static, player capsules are disabled).
    this.world.step();
  }

  addCapsule(eid: number, radius: number, height: number) {
    this.removeCapsule(eid);
    const half = Math.max(0.01, height / 2 - radius);
    // Disabled: never enters the broad phase, so every world's BVH contains only the static map.
    // (Extra colliders change BVH layout and thus contact resolution order — that broke
    // server/client prediction determinism.) The character controller still uses its shape.
    const col = this.world.createCollider(
      RAPIER.ColliderDesc.capsule(half, radius).setCollisionGroups(groups(G_PLAYER, 0)).setSensor(true).setEnabled(false),
    );
    this.capsules.set(eid, { col, radius, half });
  }

  resizeCapsule(eid: number, radius: number, height: number) {
    this.addCapsule(eid, radius, height);
  }

  removeCapsule(eid: number) {
    const c = this.capsules.get(eid);
    if (c) {
      this.world.removeCollider(c.col, false);
      this.capsules.delete(eid);
    }
  }

  /** Sets capsule height (crouch). Returns false if there's no headroom to grow. */
  setCapsuleHeight(eid: number, feet: Vec3, height: number): boolean {
    const c = this.capsules.get(eid)!;
    const half = Math.max(0.01, height / 2 - c.radius);
    if (half > c.half) {
      const shape = new RAPIER.Capsule(half, c.radius);
      const center = { x: feet.x, y: feet.y + half + c.radius + 0.02, z: feet.z };
      const hit = this.world.intersectionWithShape(center, IDENTITY_ROT, shape, undefined, MAP_ONLY);
      if (hit) return false;
    }
    c.half = half;
    c.col.setHalfHeight(half);
    return true;
  }

  /** Moves a player's capsule (feet position) by `desired`, sliding along the map. */
  move(eid: number, feet: Vec3, desired: Vec3): MoveResult {
    const c = this.capsules.get(eid)!;
    let want = desired;
    let floor = -Infinity;
    if (this.obstacles.length) ({ desired: want, floor } = this.resolveObstacles(c.radius, 2 * (c.half + c.radius), feet, desired));
    c.col.setTranslation({ x: feet.x, y: feet.y + c.half + c.radius, z: feet.z });
    this.cc.computeColliderMovement(c.col, want, undefined, MAP_ONLY);
    const m = this.cc.computedMovement();
    let dy = m.y;
    let grounded = this.cc.computedGrounded();
    if (feet.y + dy <= floor) {
      dy = floor - feet.y;
      grounded = true;
    }
    c.col.setTranslation({ x: feet.x + m.x, y: feet.y + dy + c.half + c.radius, z: feet.z + m.z });
    return { dx: m.x, dy, dz: m.z, grounded };
  }

  /**
   * Clips a move against the obstacles before Rapier sees it (so walls still win): lands the
   * capsule on any top it's coming down onto, and pushes it out of any side it overlaps — which
   * is also how a moving payload shoves players. Returns the new move and the floor (top) it
   * stands on, if any.
   */
  private resolveObstacles(r: number, height: number, feet: Vec3, desired: Vec3): { desired: Vec3; floor: number } {
    let tx = feet.x + desired.x;
    let ty = feet.y + desired.y;
    let tz = feet.z + desired.z;
    let floor = -Infinity;
    for (const o of this.obstacles) {
      const top = o.y + o.h;
      const dx = tx - o.x;
      const dz = tz - o.z;
      const u = dx * o.fx + dz * o.fz;
      const v = dz * o.fx - dx * o.fz;
      const cu = u < -o.hl ? -o.hl : u > o.hl ? o.hl : u;
      const cv = v < -o.hw ? -o.hw : v > o.hw ? o.hw : v;
      const du = u - cu;
      const dv = v - cv;
      const d2 = du * du + dv * dv;
      // Standing on it, or coming down onto it: the top holds you while you're mostly over it.
      if (feet.y >= top - TOP_SKIN && ty <= top && d2 < r * r * 0.25) {
        if (top > floor) floor = top;
        continue;
      }
      if (ty >= top - SIDE_SKIN || ty + height <= o.y || d2 >= r * r) continue;
      let nu = u;
      let nv = v;
      if (d2 > 1e-12) {
        const k = r / Math.sqrt(d2);
        nu = cu + du * k;
        nv = cv + dv * k;
      } else {
        // Centre inside the footprint: out through the nearest side.
        const pu = o.hl - (u < 0 ? -u : u);
        const pv = o.hw - (v < 0 ? -v : v);
        if (pu < pv) nu = u < 0 ? -(o.hl + r) : o.hl + r;
        else nv = v < 0 ? -(o.hw + r) : o.hw + r;
      }
      tx = o.x + nu * o.fx - nv * o.fz;
      tz = o.z + nu * o.fz + nv * o.fx;
    }
    if (ty < floor) ty = floor;
    return { desired: { x: tx - feet.x, y: ty - feet.y, z: tz - feet.z }, floor };
  }

  /**
   * Ray vs the obstacles, each grown by `grow` (sphere casts). Rays that start inside one pass
   * out of it: a third-person camera tucked against the payload still aims.
   */
  private rayObstacles(origin: Vec3, dir: Vec3, maxDist: number, grow: number): { t: number; normal: Vec3 } | null {
    let best: { t: number; normal: Vec3 } | null = null;
    for (const o of this.obstacles) {
      const rx = origin.x - o.x;
      const rz = origin.z - o.z;
      const half = o.h / 2;
      const slabs = [
        [rx * o.fx + rz * o.fz, dir.x * o.fx + dir.z * o.fz, o.hl + grow],
        [rz * o.fx - rx * o.fz, dir.z * o.fx - dir.x * o.fz, o.hw + grow],
        [origin.y - (o.y + half), dir.y, half + grow],
      ] as const;
      let tmin = 0;
      let tmax = best ? best.t : maxDist;
      let axis = -1;
      let side = 0;
      let miss = false;
      for (let i = 0; i < 3 && !miss; i++) {
        const [p, d, e] = slabs[i]!;
        if (d > -1e-12 && d < 1e-12) {
          if (p < -e || p > e) miss = true;
          continue;
        }
        let t1 = (-e - p) / d;
        let t2 = (e - p) / d;
        let s = -1;
        if (t1 > t2) {
          const t = t1;
          t1 = t2;
          t2 = t;
          s = 1;
        }
        if (t1 > tmin) {
          tmin = t1;
          axis = i;
          side = s;
        }
        if (t2 < tmax) tmax = t2;
        if (tmin > tmax) miss = true;
      }
      if (miss || axis < 0) continue;
      const normal =
        axis === 0 ? { x: o.fx * side, y: 0, z: o.fz * side } : axis === 1 ? { x: -o.fz * side, y: 0, z: o.fx * side } : { x: 0, y: side, z: 0 };
      best = { t: tmin, normal };
    }
    return best;
  }

  /** Ray vs static map and obstacles. Returns hit distance or -1. */
  raycast(origin: Vec3, dir: Vec3, maxDist: number): number {
    const hit = this.world.castRay(new RAPIER.Ray(origin, dir), maxDist, true, undefined, MAP_ONLY);
    const t = hit ? hit.timeOfImpact : -1;
    if (!this.obstacles.length) return t;
    const o = this.rayObstacles(origin, dir, t >= 0 ? t : maxDist, 0);
    return o ? o.t : t;
  }

  /** Sphere cast vs static map and obstacles (projectiles with radius). */
  sphereCast(origin: Vec3, dir: Vec3, radius: number, maxDist: number): { toi: number; normal: Vec3 } | null {
    const hit = this.world.castShape(origin, IDENTITY_ROT, dir, new RAPIER.Ball(radius), 0, maxDist, true, undefined, MAP_ONLY);
    const res = hit ? { toi: hit.time_of_impact, normal: hit.normal2 as Vec3 } : null;
    if (!this.obstacles.length) return res;
    const o = this.rayObstacles(origin, dir, res ? res.toi : maxDist, radius);
    return o ? { toi: o.t, normal: o.normal } : res;
  }

  /** Line of sight between two points (true = unobstructed). */
  los(a: Vec3, b: Vec3): boolean {
    const d = { x: b.x - a.x, y: b.y - a.y, z: b.z - a.z };
    const l = Math.hypot(d.x, d.y, d.z);
    if (l < 1e-6) return true;
    return this.raycast(a, { x: d.x / l, y: d.y / l, z: d.z / l }, l) < 0;
  }

  free() {
    this.world.free();
  }
}

const IDENTITY_ROT = { x: 0, y: 0, z: 0, w: 1 };
